/** Shared opponent-demand derivation and presentation for draft-night views. */

import { buildOpponentDemandForecast, type OpponentDemandForecast, type OpponentDemandPosition, type OpponentDemandTeamInput } from '../core/opponentDemand.ts';
import { isExcludedFromCurrentSeason } from '../core/injuries.ts';
import type { PlayerId, Position } from '../core/types.ts';
import type { AppState } from '../state/store.ts';
import { el, clear, fmt } from './dom.ts';

export function opponentDemandForState(state: AppState): OpponentDemandForecast | null {
  const { draft, league, universe, draftContext, adpIndex } = state;
  if (!draft || !league || !universe) return null;

  const teams: OpponentDemandTeamInput[] = Array.from({ length: league.teams }, (_, index) => {
    const draftSlot = index + 1;
    const rosterId = draftContext?.draft.slot_to_roster_id?.[String(draftSlot)] ?? draftSlot;
    return {
      roster_id: rosterId,
      draft_slot: draftSlot,
      team_name: draftContext?.rosterNames.get(rosterId) ?? `Team ${draftSlot}`,
      player_ids: (draft.all_rosters[rosterId] ?? []).filter((id) => !state.availabilityOverrides.has(id)),
    };
  });

  const drafted = new Set<PlayerId>();
  for (const pick of draft.picks) {
    if (!state.availabilityOverrides.has(pick.player_id)) drafted.add(pick.player_id);
  }
  for (const players of Object.values(draft.all_rosters)) {
    for (const playerId of players) {
      if (!state.availabilityOverrides.has(playerId)) drafted.add(playerId);
    }
  }

  const availablePlayerIds = new Set(
    [...universe.keys()].filter((playerId) =>
      !drafted.has(playerId) &&
      !isExcludedFromCurrentSeason(state.playerAvailability.get(playerId), league.league_type),
    ),
  );

  return buildOpponentDemandForecast({
    draft,
    league,
    teams,
    universe,
    adpIndex,
    availablePlayerIds,
  });
}

export function demandForPosition(
  forecast: OpponentDemandForecast | null,
  position: Position | null,
): OpponentDemandPosition | null {
  if (!forecast || !position) return null;
  return forecast.positions.find((entry) => entry.position === position) ?? null;
}

export interface OpponentDemandRenderOptions {
  open?: boolean;
  onToggle?: (open: boolean) => void;
}

export function renderOpponentDemandSummary(
  forecast: OpponentDemandForecast | null,
  detail: 'compact' | 'full',
  options: OpponentDemandRenderOptions = {},
): HTMLElement {
  const label = forecast
    ? detail === 'compact'
      ? `Before #${forecast.target_pick_no}: ${forecast.positions.slice(0, 4).map((position) => forecast.mode === 'market'
        ? `${position.position} ${position.projected_picks ?? 0}`
        : `${position.position} need`).join(' · ') || 'no opposing picks forecast'}`
      : `Before #${forecast.target_pick_no}: ${forecast.picks_projected} picks modeled · ${confidenceLabel(forecast)}`
    : 'Before your next pick: no opposing picks forecast';
  const details = el(
    'details',
    { class: `opponent-demand-summary ${detail}`, role: 'note', 'aria-label': 'Opponent demand forecast', open: options.open === true },
  );
  const summary = el(
    'summary',
    { class: 'opponent-demand-summary-toggle' },
    el('strong', {}, label),
    forecast && detail === 'compact' ? el('span', { class: 'opponent-demand-summary-count' }, ` · ${forecast.picks_projected}/${forecast.picks_before_target} modeled`) : null,
  );
  details.appendChild(summary);

  if (!forecast) {
    details.appendChild(el('div', { class: 'opponent-demand-body' }, el('span', {}, 'No opposing picks are currently forecast before your next turn.')));
    details.addEventListener('toggle', () => options.onToggle?.(details.open));
    return details;
  }

  const market = forecast.mode === 'market';
  const positionChips = forecast.positions.slice(0, detail === 'full' ? 6 : 4).map((position) =>
    el(
      'span',
      {
        class: ['opponent-demand-chip', position.pressure ? `pressure-${position.pressure}` : '', `pos-${position.position.toLowerCase()}`].filter(Boolean).join(' '),
        title: market
          ? `${position.team_names.join(', ') || 'No named team'} · ${position.projected_picks ?? 0} projected ${position.position} pick${position.projected_picks === 1 ? '' : 's'}`
          : `${position.team_names.join(', ') || 'No named team'} · open ${position.position} need`,
      },
      market ? `${position.position} ${position.projected_picks ?? 0} projected` : `${position.position} need`,
      position.teams_needing > 0 ? el('small', {}, ` ${position.teams_needing} team${position.teams_needing === 1 ? '' : 's'} need`) : null,
    ),
  );

  const header = el(
    'div',
    { class: 'opponent-demand-head' },
    el('strong', {}, `Opponent demand before pick #${forecast.target_pick_no}`),
    el('span', { class: 'opponent-demand-confidence' }, market
      ? `${forecast.picks_projected} of ${forecast.picks_before_target} picks modeled · ${confidenceLabel(forecast)}`
      : 'Roster needs only · no ADP projection'),
  );
  const chips = el('div', { class: 'opponent-demand-chips', 'aria-label': 'Projected opponent positional demand' }, ...positionChips);
  const exposed = forecast.exposed_players.length > 0
      ? el(
        'p',
        { class: 'opponent-demand-exposed' },
        el('strong', {}, 'Projected:'),
        ` ${forecast.exposed_players.slice(0, detail === 'full' ? 5 : 3).map((player) => `#${player.projected_pick_no} ${player.name} (${player.position} ${fmt.pct(player.gone_probability)} market risk)`).join(' · ')}`,
      )
    : null;

  const teams = detail === 'full'
    ? el(
      'div',
      { class: 'opponent-demand-teams', 'aria-label': 'Upcoming opponent demand by pick' },
      ...forecast.teams.map((team) =>
        el(
          'span',
          { class: 'opponent-demand-team' },
          `#${team.pick_no} ${team.team_name}: `,
          team.likely_positions.map((entry) => entry.probability === null ? entry.position : `${entry.position} ${fmt.pct(entry.probability)}`).join(' · ') || 'mixed needs',
          team.selected_player ? ` → ${team.selected_player.name}` : '',
        ),
      ),
    )
    : null;

  details.appendChild(
    el(
      'div',
      { class: 'opponent-demand-body' },
      header,
      chips,
      teams,
      exposed,
      el('span', { class: 'opponent-demand-disclaimer' }, market
        ? detail === 'full'
          ? 'Estimate from open roster slots and ADP, not knowledge of manager behavior. Recommendations and return odds are unchanged.'
          : 'Roster pressure + ADP estimate; it does not change recommendations.'
        : 'ADP is unavailable, so this view shows roster needs only. Recommendations and return odds are unchanged.'),
    ),
  );
  details.addEventListener('toggle', () => options.onToggle?.(details.open));
  return details;
}

function confidenceLabel(forecast: OpponentDemandForecast): string {
  switch (forecast.confidence) {
    case 'high': return 'strong market signal';
    case 'medium': return 'moderate market signal';
    case 'low': return 'thin market signal';
    default: return 'roster-only signal';
  }
}

/** Replace an existing summary in a stable shell without disturbing focus. */
export function updateOpponentDemandSummary(
  container: HTMLElement,
  forecast: OpponentDemandForecast | null,
  detail: 'compact' | 'full' = 'compact',
  options: OpponentDemandRenderOptions = {},
): void {
  clear(container);
  container.className = 'opponent-demand-host';
  container.appendChild(renderOpponentDemandSummary(forecast, detail, options));
}
